import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const KEY = "cookie_consent_v1";

export function getConsent() {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getConsent()) setOpen(true);
    const reopen = () => {
      const c = getConsent();
      setAnalytics(c?.analytics ?? false);
      setMarketing(c?.marketing ?? false);
      setCustomize(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-preferences", reopen);
    return () => window.removeEventListener("open-cookie-preferences", reopen);
  }, []);

  const save = (prefs) => {
    localStorage.setItem(KEY, JSON.stringify({ necessary: true, ...prefs, ts: new Date().toISOString() }));
    setOpen(false);
    setCustomize(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[1300] no-print p-3 sm:p-4" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-card shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Cookie className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-lg font-semibold">Rispettiamo la tua privacy</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Usiamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie per statistiche e miglioramento del servizio. Puoi accettarli tutti, rifiutare quelli non essenziali o scegliere le tue preferenze. Leggi la <Link to="/cookie" className="text-primary underline">Cookie Policy</Link> e la <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.
            </p>

            {customize && (
              <div className="mt-4 space-y-3" data-testid="cookie-customize">
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div><p className="text-sm font-medium">Cookie necessari</p><p className="text-xs text-muted-foreground">Indispensabili per l'accesso e la sicurezza. Sempre attivi.</p></div>
                  <Switch checked disabled data-testid="cookie-necessary" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div><p className="text-sm font-medium">Cookie analitici</p><p className="text-xs text-muted-foreground">Statistiche anonime di utilizzo per migliorare il sito.</p></div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-analytics" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div><p className="text-sm font-medium">Cookie di marketing</p><p className="text-xs text-muted-foreground">Contenuti e comunicazioni personalizzati.</p></div>
                  <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="cookie-marketing" />
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              {!customize ? (
                <>
                  <Button data-testid="cookie-accept-all" onClick={() => save({ analytics: true, marketing: true })} className="bg-primary hover:bg-primary/90">Accetta tutti</Button>
                  <Button data-testid="cookie-reject" variant="outline" onClick={() => save({ analytics: false, marketing: false })}>Rifiuta non essenziali</Button>
                  <Button data-testid="cookie-customize-open" variant="ghost" onClick={() => setCustomize(true)}>Personalizza</Button>
                </>
              ) : (
                <>
                  <Button data-testid="cookie-save" onClick={() => save({ analytics, marketing })} className="bg-primary hover:bg-primary/90">Salva preferenze</Button>
                  <Button data-testid="cookie-accept-all-2" variant="outline" onClick={() => save({ analytics: true, marketing: true })}>Accetta tutti</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
